import React, { FC, useEffect } from 'react'
import {
  BrowserRouter,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from 'react-router'
import { MainLayout } from './layout/MainLayout'
import { useWalletConnection } from './hooks/useWalletConnection'
import { useTonProofSetup } from './hooks/useTonProofSetup'
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary'
import {
  CreateMoneyPoolPage,
  createMoneyPoolPagePath,
} from './pages/CreateMoneyPoolPage'
import { FaqPage, faqPagePath } from './pages/FaqPage'
import { IndexPage, indexPagePath } from './pages/IndexPage'
import { LanguagePage, languagePagePath } from './pages/LanguagePage'
import { NotFoundPage } from './pages/NotFoundPage'
import {
  SingleBoxPage,
  singleBoxPageBoxPath,
  singleBoxPagePath,
} from './pages/SingleBoxPage'
import { OnboardingPage, onboardingPagePath } from './pages/OnboardingPage'
import {
  BonusPage,
  bonusTasksPagePath,
  bonusReferralsPagePath,
  BonusTab,
} from './pages/BonusPage'
import { parseStartParam } from './functions/startParam'
import { useOnboardingContext } from './contexts/OnboardingContext'
import { useAppContext } from './contexts/App.context'
import {
  profileMyBoxesPagePath,
  ProfilePage,
  profileVotesPagePath,
} from './pages/ProfilePage'
import { ProfileBoxTypeTab } from './pages/ProfilePage'
import { useTelegram } from './hooks/useTelegram'
import { styled } from '@linaria/react'
import { useViewport } from './hooks/useViewport'
import { useTelegramInit } from './hooks/useTelegramInit'
import { APP_NAME } from './constants'
import { VersionPage, versionPagePath } from './pages/VersionPage'

const AppRoutes: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  useTelegramInit()
  const { tg, isTelegramVersionSupported } = useTelegram()

  // initial settings for TMA
  useEffect(() => {
    // BackButton is only available in Telegram WebApp version 6.1+
    if (!isTelegramVersionSupported(6.1)) {
      return
    }

    if (location.pathname === indexPagePath) {
      tg.BackButton.hide()
    } else {
      const handleBack = () => {
        navigate(-1)
      }

      tg.BackButton.show()
      tg.BackButton.onClick(handleBack)

      return () => {
        tg.BackButton.offClick(handleBack)
      }
    }
  }, [tg, navigate, location.pathname])

  const { isCompleted: isOnboardingCompleted } = useOnboardingContext()
  useTonProofSetup() // Setup TON Connect proof before wallet connection
  useWalletConnection()

  // перенаправление на страницу с боксом
  useEffect(() => {
    const startParam = tg.initDataUnsafe.start_param
    const { box } = parseStartParam(startParam)

    if (box) {
      navigate(`${singleBoxPageBoxPath}${box}`, { replace: true })
      return
    }
    // Должно сработать 1 раз
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Если onboarding пройден, переходим на главную
  useEffect(() => {
    if (
      isOnboardingCompleted === true &&
      location.pathname === onboardingPagePath
    ) {
      navigate(indexPagePath)
      return
    }
  }, [isOnboardingCompleted, navigate, location.pathname])

  return (
    <Routes>
      <Route path={onboardingPagePath} element={<OnboardingPage />} />

      <Route element={<MainLayout headerMode="full" isShowBottomNavigation />}>
        <Route
          path={profileVotesPagePath}
          element={<ProfilePage activeTab={ProfileBoxTypeTab.Votes} />}
        />

        <Route
          path={profileMyBoxesPagePath}
          element={<ProfilePage activeTab={ProfileBoxTypeTab.MyBoxes} />}
        />
      </Route>

      <Route
        element={
          <MainLayout headerMode="hide" isShowBottomNavigation={false} />
        }
      >
        <Route
          path={createMoneyPoolPagePath}
          element={<CreateMoneyPoolPage />}
        />
      </Route>

      <Route
        element={<MainLayout headerMode="with-logo" isShowBottomNavigation />}
      >
        <Route index path={indexPagePath} element={<IndexPage />} />
        <Route path={`${indexPagePath}/${APP_NAME}`} element={<IndexPage />} />
      </Route>

      <Route
        element={<MainLayout headerMode="with-logo" isShowBottomNavigation />}
      >
        <Route path={singleBoxPagePath} element={<SingleBoxPage />} />

        <Route
          path={bonusTasksPagePath}
          element={<BonusPage activeTab={BonusTab.Tasks} />}
        />

        <Route
          path={bonusReferralsPagePath}
          element={<BonusPage activeTab={BonusTab.Referrals} />}
        />

        <Route path={faqPagePath} element={<FaqPage />} />
        <Route path={languagePagePath} element={<LanguagePage />} />
      </Route>

      <Route path={versionPagePath} element={<VersionPage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export const App: FC = () => {
  const { basePath } = useAppContext()
  const { safeAreaTop } = useViewport()

  return (
    <ErrorBoundary>
      <BrowserRouter basename={basePath}>
        <FullSizeAppContainer $paddingTop={safeAreaTop}>
          <AppRoutes />
        </FullSizeAppContainer>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

const FullSizeAppContainer = styled.div<{ $paddingTop: number }>`
  padding-top: ${props => `${props.$paddingTop}px`};
  min-width: 320px;
`
